import React, { useState } from 'react';
import { isEmpty } from 'lodash';

import ClaimLink from '~/components/ClaimLink';
import { getClaimNotificationIcon } from '~/components/ClaimNotificationsCard/ClaimNotificationCardUtils';
import { adjustNotificationColumnsByUserOrg } from '~/components/ClaimNotificationsCard/ClaimNotificationUtils';
import type { ClaimNotificationModel, UpdateNotification } from '~/components/ClaimNotificationsCard/types';
import { ContactEntity } from '~/components/Contact';
import { FsIconButton } from '~/components/core';
import SortableTable from '~/components/core/Tables/SortableTable';
import type { PaginationProps } from '~/components/core/Tables/SortableTable/TablePaginationSection/types';
import { ExposureDetailedTitle } from '~/components/exposures/ExposureDetailedTitle';
import { useCms } from '~/components/hooks/useCms';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { ThreeDotsHorizontalIcon } from '~/components/icons';
import NotificationAssignee from '~/components/Notifications/Assignee';
import CategoryEditableChip from '~/components/Notifications/CategoryEditableChip';
import DismissButton from '~/components/Notifications/DismissButton';
import NotificationDueDate from '~/components/Notifications/NotificationDueDate';
import PriorityEditableChip from '~/components/Notifications/PriorityEditableChip';
import type { NotificationsTableDisplayOptions } from '~/components/Notifications/types';
import {
  changeClaimNotificationDismissState,
  isUnauthorizedPredicate,
  unauthorizedContentMessage,
} from '~/components/Notifications/utils';
import useOrganization from '~/components/OrganizationContext';
import type { CategoryModel } from '~/components/SystemConfiguration/NotificationsConfiguration/NotificationsCategories';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { serverDateToLocal } from '~/DateTimeUtils';
import { stringCmp } from '~/Utils';
import { getLobIcon } from '~/Utils/lobUtils';

import type { ExposureModel } from '../types/exposure-types';

import ShowNotificationOnClickWrapper from './ShowNotificationOnClickWrapper';

interface NotificationsTableProps {
  notifications: ClaimNotificationModel[];
  displayOptions: NotificationsTableDisplayOptions;
  exposuresDict: { [id: number]: ExposureModel };
  pagination: PaginationProps;
  onSortByColumn: (e: React.MouseEvent<HTMLSpanElement>, sortProps: { id: string; order: 'desc' | 'asc' }) => void;
  disableSort?: boolean;
  defaultSortOrder: 'desc' | 'asc';
  onHandleShowNotification: (id: number) => Promise<void>;
  notificationsReadStatusDict: { [notification_id: number]: boolean };
  onlyDismissed?: boolean;
  notificationIdToIsSelectedMap: { [notification_id: number]: boolean };
  setNotificationIdToIsSelectedMap: (newValue: { [notification_id: number]: boolean }) => void;
  defaultSortColumn: string;
  markOverdue?: boolean;
  counterViewText?: React.ReactNode;
  onUpdateNotification: UpdateNotification;
}

const NotificationsTable: React.FC<NotificationsTableProps> = ({
  notifications,
  displayOptions,
  exposuresDict,
  pagination,
  onSortByColumn,
  disableSort,
  defaultSortOrder,
  onHandleShowNotification,
  notificationsReadStatusDict,
  onlyDismissed,
  notificationIdToIsSelectedMap,
  setNotificationIdToIsSelectedMap,
  defaultSortColumn,
  markOverdue,
  counterViewText,
  onUpdateNotification,
}) => {
  const [notificationIdsSubmitting, setNotificationIdsSubmitting] = useState<number[]>([]);

  const { userOrganization } = useCms();
  const { isLoading: isLoadingLobConfiguration, lobConfigurationsDict } = useLobConfiguration();
  const { notificationCategories } = useOrganization() as {
    notificationCategories: CategoryModel[];
  };

  function claimNotificationClaimTypeIcon(notification: ClaimNotificationModel) {
    if (!notification.claim_id || !notification.claim_lob || isLoadingLobConfiguration) {
      return null;
    }

    return getLobIcon({ lob: notification.claim_lob, lobConfigurationsDict });
  }

  const setNotificationAsSubmitted = (id: number) => setNotificationIdsSubmitting((prev) => [...prev, id]);
  const setNotificationAsNotSubmitted = (id: number) =>
    setNotificationIdsSubmitting((prev) => prev.filter((currId) => currId !== id));

  React.useEffect(() => {
    setNotificationIdsSubmitting([]);
  }, [notifications]);

  const columns = [
    {
      id: 'select_checkbox',
      label: (
        <Checkbox
          disabled={notificationIdsSubmitting.length > 0 || notifications.length === 0}
          className="ml-[6px]"
          checked={
            notifications.length > 0 &&
            notifications?.every((notification) => notification?.id && notificationIdToIsSelectedMap[notification.id])
          }
          onChange={(_event, newValue) => {
            if (!notifications) {
              return;
            }

            const newMap = { ...notificationIdToIsSelectedMap };

            if (newValue) {
              // Select all notifications in current page
              notifications.forEach((notification) => {
                if (notification?.id) {
                  newMap[notification.id] = true;
                }
              });
            } else {
              // Deselect all notifications in current page
              notifications.forEach((notification) => {
                if (notification?.id && notification.id in newMap) {
                  delete newMap[notification.id];
                }
              });
            }

            setNotificationIdToIsSelectedMap(newMap);
          }}
        />
      ),
      width: 25,
      disableSort: true,
      specialCell: (notification: ClaimNotificationModel) => (
        <Checkbox
          checked={Boolean(notification?.id && notificationIdToIsSelectedMap[notification.id])}
          onChange={(_event, newValue) => {
            if (!notification?.id) {
              return;
            }
            const newState = { ...notificationIdToIsSelectedMap };
            if (newValue) {
              newState[notification.id] = true;
            } else {
              delete newState[notification.id];
            }
            setNotificationIdToIsSelectedMap(newState);
          }}
        />
      ),
    },
    {
      id: 'dismiss_action',
      label: '',
      width: 25,
      disableSort: true,
      specialCell: (notification: ClaimNotificationModel) => (
        <DismissButton
          notification={notification}
          onClick={() =>
            changeClaimNotificationDismissState(
              notification,
              () => setNotificationAsSubmitted(notification.id),
              () => setNotificationAsNotSubmitted(notification.id),
              (id, state) => onUpdateNotification(id, { datetime_dismissed: state })
            )
          }
          disabled={notificationIdsSubmitting.includes(notification.id)}
        />
      ),
    },
    {
      id: 'type',
      label: 'Type',
      width: 15,
      disableSort: true,
      specialCell: (notification: ClaimNotificationModel) => getClaimNotificationIcon(notification, undefined, 20),
    },
    {
      id: 'priority',
      label: 'Priority',
      width: 100,
      specialCell: (notification: ClaimNotificationModel) => (
        <PriorityEditableChip
          notification={notification}
          disabled={notificationIdsSubmitting.includes(notification.id)}
          setIsUpdating={(isUpdating) => {
            if (isUpdating) {
              setNotificationAsSubmitted(notification.id);
            } else {
              setNotificationAsNotSubmitted(notification.id);
            }
          }}
          onUpdate={onUpdateNotification}
        />
      ),
    },
    {
      id: 'due_date',
      label: 'Due Date',
      specialCell: (notification: ClaimNotificationModel) => (
        <NotificationDueDate
          notification={notification}
          disabled={notificationIdsSubmitting.includes(notification.id)}
          setIsUpdating={(isUpdating) => {
            if (isUpdating) {
              setNotificationAsSubmitted(notification.id);
            } else {
              setNotificationAsNotSubmitted(notification.id);
            }
          }}
          onUpdate={onUpdateNotification}
          markOverdue={markOverdue}
        />
      ),
    },
    {
      id: 'sub_organization',
      label: 'Sub-Organization',
      specialCell: (notification: ClaimNotificationModel) => notification.sub_organization_name,
      isHidden: !userOrganization.sub_organizations_enabled,
    },
    {
      id: 'claim_id',
      label: 'Claim',
      specialCell: (notification: ClaimNotificationModel) =>
        notification.claim_id ? (
          <div className="flex flex-nowrap items-center">
            {claimNotificationClaimTypeIcon(notification)}
            <span className="pl-4">
              <ClaimLink claimId={notification.claim_id} linkText={notification.claim_id_display || ''} />
            </span>
          </div>
        ) : null,
      isHidden: displayOptions.showClaim,
    },
    {
      id: 'insured_contact_full_name',
      label: 'Insured Name',
      disableSort: true,
      specialCell: (claimNotification: ClaimNotificationModel) =>
        claimNotification.insured_contact_id ? (
          <ContactEntity
            contactId={claimNotification.insured_contact_id}
            contactDisplayName={claimNotification.insured_contact_full_name}
          />
        ) : null,
      specialCmpFunc: (claimNotification1: ClaimNotificationModel, claimNotification2: ClaimNotificationModel) =>
        stringCmp(
          claimNotification1.insured_contact_full_name || '',
          claimNotification2.insured_contact_full_name || ''
        ),
      hideRestrictedContent: true,
    },
    {
      id: 'label_text',
      label: 'Exposure',
      disableSort: true,
      specialCell: (notification: ClaimNotificationModel) =>
        notification.exposure_id && exposuresDict[notification.exposure_id] ? (
          <ExposureDetailedTitle exposure={exposuresDict[notification.exposure_id]} withMinorChip={false} />
        ) : null,
      isHidden: !displayOptions.showExposure,
      hideRestrictedContent: true,
    },
    {
      id: 'title',
      label: 'Title',
      hideRestrictedContent: true,
      specialCell: (claimNotification: ClaimNotificationModel) => (
        <ShowNotificationOnClickWrapper
          claimNotification={claimNotification}
          onHandleShowNotification={onHandleShowNotification}
        >
          {claimNotification.title}
        </ShowNotificationOnClickWrapper>
      ),
    },
    {
      id: 'recipient_user',
      label: 'Assignee',
      specialCell: (claimNotification: ClaimNotificationModel) => (
        <ShowNotificationOnClickWrapper
          claimNotification={claimNotification}
          onHandleShowNotification={onHandleShowNotification}
        >
          <NotificationAssignee notification={claimNotification} />
        </ShowNotificationOnClickWrapper>
      ),
    },
    {
      id: 'created_by_username',
      label: 'Creator',
      disableSort: true,
      specialCell: (claimNotification: ClaimNotificationModel) => (
        <ShowNotificationOnClickWrapper
          claimNotification={claimNotification}
          onHandleShowNotification={onHandleShowNotification}
        >
          {claimNotification.created_by_username || ''}
        </ShowNotificationOnClickWrapper>
      ),
    },
    {
      id: 'datetime',
      label: 'Created Date',
      specialCell: (claimNotification: ClaimNotificationModel) => (
        <ShowNotificationOnClickWrapper
          claimNotification={claimNotification}
          onHandleShowNotification={onHandleShowNotification}
        >
          {claimNotification.datetime ? serverDateToLocal(claimNotification.datetime) : ''}
        </ShowNotificationOnClickWrapper>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      specialCell: (notification: ClaimNotificationModel) => (
        <CategoryEditableChip
          claimNotification={notification}
          disabled={notificationIdsSubmitting.includes(notification.id)}
          setIsUpdating={() => setNotificationAsSubmitted(notification.id)}
          onUpdate={onUpdateNotification}
        />
      ),
      disableSort: true,
      isHidden: isEmpty(notificationCategories),
    },
    {
      id: 'datetime_dismissed',
      label: 'Date Dismissed',
      specialCell: (claimNotification: ClaimNotificationModel) => (
        <ShowNotificationOnClickWrapper
          claimNotification={claimNotification}
          onHandleShowNotification={onHandleShowNotification}
        >
          {claimNotification.datetime_dismissed ? serverDateToLocal(claimNotification.datetime_dismissed) : ''}
        </ShowNotificationOnClickWrapper>
      ),
      isHidden: !onlyDismissed,
      hideRestrictedContent: true,
    },
    {
      id: 'dismissing_user',
      label: 'Dismissed By',
      isHidden: !onlyDismissed,
      specialCell: (claimNotification: ClaimNotificationModel) => (
        <ShowNotificationOnClickWrapper
          claimNotification={claimNotification}
          onHandleShowNotification={onHandleShowNotification}
        >
          {claimNotification.dismissing_user || ''}
        </ShowNotificationOnClickWrapper>
      ),
    },
    {
      id: 'view_action',
      width: 25,
      disableSort: true,
      specialCell: (notification: ClaimNotificationModel) => (
        <FsIconButton
          icon={ThreeDotsHorizontalIcon}
          tooltipText="View more details"
          className="cursor-pointer hover:stroke-teal-700"
          onClick={async () => await onHandleShowNotification(notification.id)}
          disabled={notificationIdsSubmitting.includes(notification.id)}
        />
      ),
      hideRestrictedContent: true,
    },
  ];

  const orderByColumnIdx = columns.findIndex((column) => column.id === defaultSortColumn);

  const unreadRowsIds = notificationsReadStatusDict
    ? notifications?.filter((notification) => !notificationsReadStatusDict?.[notification.id]).map((n) => n.id) || []
    : [];

  const dismissedRowsIds = !onlyDismissed
    ? notifications?.filter((notification) => !!notification?.datetime_dismissed).map((n) => n.id) || []
    : [];

  const columnData = adjustNotificationColumnsByUserOrg(userOrganization, columns);

  return (
    <SortableTable
      columns={columnData}
      rows={notifications}
      stickyHeader
      defaultOrderColumn={orderByColumnIdx}
      order={defaultSortOrder}
      paginationProps={pagination}
      onSortByColumn={onSortByColumn}
      disableSortByUser={disableSort}
      keepRowsOrder={disableSort}
      paginationLocation="both"
      isRestrictedPredicate={isUnauthorizedPredicate}
      restrictedContentTooltipText={unauthorizedContentMessage}
      boldRowsIds={unreadRowsIds}
      selectedRowsIds={unreadRowsIds}
      greyedOutRowsIds={notificationIdsSubmitting}
      counterViewText={counterViewText}
      strikeOutRowIds={dismissedRowsIds}
    />
  );
};

export default NotificationsTable;
